import React, { useState, useEffect } from "react";
import ErrorTextWithIcon from "../../Typography/ErrorTextWithIcon/ErrorTextWithIcon";
import { countries } from "../../../../common";
import { phoneNumberRules } from "../../../../common/Countries";

const isPhoneValid = (phone, field, setError, clearErrors) => {
    // If phone is empty, set error and return
    if (!phone) {
        setError(field, {
            type: "error",
            message: "Phone number is required",
        });
        return false;
    }

    // Remove only the `+` sign (keep other formatting if present)
    const cleanPhone = phone.replace("+", "");

    // Check if length is at least 7 characters
    if (cleanPhone.length < 7) {
        setError(field, {
            type: "error",
            message: "Phone number must be at least 7 digits",
        });
        return false;
    }

    // If valid, clear any previous error
    clearErrors(field);
    return true;
};

// Modified FloatingLabelPhoneField component with validation integration
const FloatingLabelPhoneField = ({
    errors,
    label,
    field,
    setValue,
    setError,
    clearErrors,
    watch,
    defaultValue,
    defaultCountry,
}) => {
    const [prefix, setPrefix] = useState(null);
    const [countryCode, setCountryCode] = useState(null);

    useEffect(() => {
        const selectedCountry = countries?.find(
            (country) => country?.code === defaultCountry
        );
        if (selectedCountry) {
            setPrefix(selectedCountry?.phoneCode);
            setCountryCode(selectedCountry?.code);
        }
    }, [defaultCountry]);

    const handlePrefixChange = (e) => {
        const selectedValue = e.target.value;
        const [newCountryCode, newPrefix] = selectedValue.split("-");
        const currentValue = watch(field) || "";
        const phoneWithoutPrefix = currentValue.startsWith(prefix)
            ? currentValue.slice(prefix.length)
            : currentValue;

        setPrefix(newPrefix);
        setCountryCode(newCountryCode);

        const newPhoneValue = newPrefix + phoneWithoutPrefix;
        setValue(field, newPhoneValue);
    };

    const handleInputChange = (e) => {
        // Remove non-numeric characters except '+'
        let value = e?.target?.value?.replace(/[^0-9+]/g, "");

        if (!value.startsWith(prefix)) {
            // If value starts with different prefix, remove it
            if (value.startsWith("+")) {
                value = value?.substring(1)?.replace(/^\d*/, "");
            }

            if (prefix) {
                value = prefix + value;
            } else {
                value = value;
            }
        }
        setValue(field, value);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        isPhoneValid(value, field, setError, clearErrors);
    };

    return (
        <div className="flex-1">
            <div className={`relative h-[50px] outline rounded-[1px] outline-1 outline-[#ece9ee] focus:outline-neutral-800`}>
                <div className=" absolute inset-x-0 bottom-0 flex items-center w-full px-2.5 py-2 text-gray-900 bg-transparent peer">
                    <select
                        value={`${countryCode}-${prefix}`}
                        onChange={handlePrefixChange}
                        className="bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 mr-2 flex items-center max-w-fit"
                    >
                        {countries?.map((country, index) => (
                            <option
                                key={index}
                                value={`${country?.code}-${country?.phoneCode}`}
                                className="flex items-center gap-2"
                            >
                                {country?.flag} {country?.phoneCode} (
                                {country?.code})
                            </option>
                        ))}
                    </select>
                    <input
                        id={field}
                        name={field}
                        value={watch(field)}
                        onBlur={handleBlur}
                        onChange={handleInputChange}
                        className="w-full bg-transparent border-none focus:outline-none focus:ring-0"
                    />
                </div>
                <label
                    htmlFor={field}
                    className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-[15px] z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-gray-600 peer-focus:dark:text-gray-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                >
                    {label}
                </label>
            </div>
            <ErrorTextWithIcon text={errors?.[field]?.message} />
        </div>
    );
};

export default FloatingLabelPhoneField;
